/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * The saga for Money Transfer wizard
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.2.0
 */
import { createElement } from 'react';

import get from 'lodash/get';
import set from 'lodash/set';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';

import { makeSelectBreadCrumbsData } from '@/main/modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@/main/modules/breadCrumbs/utils/breadCrumbsGoBack';
import { ModalMultipleOrders } from '@/main/modules/modal/components/ModalMultipleOrders';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';

import { IAccountSelectItem } from '../../components/AccountSelect/AccountSelectTypes';
import { parseMoneyToFloat } from '../../components/common/MoneyInput/utils/parseMoneyToFloat';
import { API_HOSTNAME, WIZARD, WIZARD_ACTION } from '../../modules/app/Api';
import { HttpStatus } from '../../modules/app/appConstants';
import {
    makeSelectMoneyTransferTranslation,
    makeSelectTranslationCommon,
    makeSelectWizardTranslation,
} from '../../modules/internationalization/intSelectors';
import ICommonTranslation from '../../modules/internationalization/types/common';
import { IMoneyTransferTranslation } from '../../modules/internationalization/types/wizards/moneyTransfer';
import { IWizardTranslation } from '../../modules/internationalization/types/wizards/wizard';
import ModalErrorCommon from '../../modules/modal/components/ModalError';
import { modalOpen } from '../../modules/modal/modalActions';
import { IModalRenderProps } from '../../modules/modal/types/modalTypes';
import {
    EWizardReportParam,
    mapWizardReportApiToParams,
} from '../../modules/wizard/components/WizardReport/WizardReportConstants';
import {
    wizardReportStorageRestoreSaga,
    wizardReportValidateSaga,
} from '../../modules/wizard/components/WizardReport/wizardReportSagas';
import {
    makeSelectWizardReportOrderParams,
    makeSelectWizardReportParam,
} from '../../modules/wizard/components/WizardReport/wizardReportSelectors';
import {
    IWizardReportOrderParams,
    IWizardReportParam,
} from '../../modules/wizard/components/WizardReport/wizardReportTypes';
import { parseWizardValidationErrors } from '../../modules/wizard/utils/parseWizardValidationErrors';
import {
    wizardNextAction,
    wizardOpenModalSummaryAction,
    wizardReportSetErrorsAction,
    wizardStorageSaveStepAction,
} from '../../modules/wizard/wizardActions';
import {
    EWizardName,
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '../../modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '../../modules/wizard/wizardSagas';
import {
    makeSelectWizardBasketSnapshotHeader,
    makeSelectWizardBasketSnapshotId,
    makeSelectWizardStepName,
} from '../../modules/wizard/wizardSelectors';
import {
    IWizardValidationError,
    TWizardBackFailedAction,
    TWizardMetaSuccessAction,
    TWizardNextFailedAction,
    TWizardNextSuccessAction,
    TWizardStartFailedAction,
} from '../../modules/wizard/wizardTypes';
import { requestSaga } from '../../sagas/requestSaga';
import { formQueryParamsString } from '../../sagas/utils/formQueryParamsString';
import { stringFormat } from '../../utils/stringFormat';
import {
    makeSelectAuthoritiesIsServiceUser,
    makeSelectIsFeatureWithVersionEnabled,
} from '../PageAuth/selectors/authSelectors';
import {
    moneyTransferCheckRecipientsFinish,
    moneyTransferCheckRecipientsStart,
    moneyTransferLoadAccountList,
    moneyTransferLoadAccountListFailed,
    moneyTransferLoadAccountListSuccessful,
    moneyTransferSetConflictsAction,
    moneyTransferSetParams,
    moneyTransferSetParamsErrorCode,
    moneyTransferStorageSaveStep,
    moneyTransferValidateSetStatus,
    moneyTransferValidateStart,
} from './moneyTransferActions';
import {
    EMoneyTransferSteps,
    EMoneyTransferStepsApiParams,
    EMoneyTransferValidationCodes,
    mapMoneyTransferStepsApiToParams,
    MONEY_TRANSFER_ACCOUNT_LIST_LENGTH,
    MONEY_TRANSFER_CHECK_RECIPIENTS_START,
    MONEY_TRANSFER_HANDLE_NEXT,
    MONEY_TRANSFER_LOAD_ACCOUNT_LIST,
    MONEY_TRANSFER_LOAD_ACCOUNT_LIST_FAILED,
    MONEY_TRANSFER_LOAD_ACCOUNT_LIST_SUCCESSFUL,
    MONEY_TRANSFER_STORAGE_SAVE_STEP,
    MONEY_TRANSFER_VALIDATE_START,
} from './moneyTransferConstants';
import { initTransferAccount } from './moneyTransferReducer';
import {
    makeSelectMoneyTransferAccountList,
    makeSelectMoneyTransferAmount,
    makeSelectMoneyTransferErrorsStepAccount,
    makeSelectMoneyTransferGetConflicts,
    makeSelectMoneyTransferSearchValue,
    makeSelectMoneyTransferSelectedAccount,
} from './moneyTransferSelectors';
import {
    IMoneyTransferValidateStartPayload,
    TMoneyTransferLoadAccountListAction,
    TMoneyTransferStorageSaveStepAction,
    TMoneyTransferValidateStartAction,
} from './types/moneyTransferActionsTypes';
import {
    EMoneyTransferParamType,
    IMoneyTransferErrorsStepAccount,
    IMoneyTransferNextAccountPayload,
    IMoneyTransferNextFinalPayload,
    mapMoneyTransferParamTypeToApiAction,
} from './types/moneyTransferTypes';

interface IResponseAccountItem {
    balance: number;
    id: number;
    name: string;
    number: string;
}

interface IResponseAccountList {
    accounts: IResponseAccountItem[];
    count: number;
}

export function* moneyTransferLoadAccountListSaga(action: TMoneyTransferLoadAccountListAction): SagaIterator {
    const { paramType, start } = action.payload;
    const searchValue: string =
        action.payload.searchValue !== undefined
            ? action.payload.searchValue
            : yield select(makeSelectMoneyTransferSearchValue(paramType));
    const list = yield select(makeSelectMoneyTransferAccountList(paramType));
    const actionParam: string = mapMoneyTransferParamTypeToApiAction[paramType];
    const transferAccountFrom: IAccountSelectItem = yield select(
        makeSelectMoneyTransferSelectedAccount(EMoneyTransferParamType.transferAccountFrom),
    );

    const header = yield select(makeSelectWizardBasketSnapshotHeader());
    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-BASKET-SNAPSHOT': header,
        },
    };

    try {
        const url =
            API_HOSTNAME + stringFormat(WIZARD, EWizardName.MONEY_TRANSFER) + stringFormat(WIZARD_ACTION, actionParam);
        const queryString = formQueryParamsString({
            from: start ? 0 : list.length,
            term: searchValue ? searchValue : '',
            size: MONEY_TRANSFER_ACCOUNT_LIST_LENGTH,
            id: paramType === EMoneyTransferParamType.transferAccountTo ? transferAccountFrom.id.toString() : '',
        });
        const { accounts }: IResponseAccountList = yield call(requestSaga, url + queryString, 'GET', null, options);
        const accountList: IAccountSelectItem[] = accounts.map((account) => {
            return {
                id: account.id.toString(),
                balance: account.balance,
                name: account.name,
                number: account.number,
            };
        });
        const fullAccountList = start ? accountList : [...list, ...accountList];

        yield put(
            moneyTransferLoadAccountListSuccessful({
                data: fullAccountList,
                hasMore: accounts.length === MONEY_TRANSFER_ACCOUNT_LIST_LENGTH,
                paramType,
            }),
        );
    } catch (e: any) {
        yield put(moneyTransferLoadAccountListFailed({ paramType, message: e }));
    }
}

export function* moneyTransferCheckRecipientsSaga(): SagaIterator {
    yield put(
        moneyTransferLoadAccountList({
            isLoading: false,
            paramType: EMoneyTransferParamType.transferAccountTo,
            searchValue: '',
            start: true,
        }),
    );
    const moneyTransferLoadAccountListAction = yield take([
        MONEY_TRANSFER_LOAD_ACCOUNT_LIST_SUCCESSFUL,
        MONEY_TRANSFER_LOAD_ACCOUNT_LIST_FAILED,
    ]);
    if (moneyTransferLoadAccountListAction.type === MONEY_TRANSFER_LOAD_ACCOUNT_LIST_FAILED) {
        yield put(moneyTransferCheckRecipientsFinish());
        return;
    }

    const accountListTo = yield select(makeSelectMoneyTransferAccountList(EMoneyTransferParamType.transferAccountTo));
    if (!accountListTo.length) {
        yield put(
            moneyTransferSetParamsErrorCode({
                accountFrom: EMoneyTransferValidationCodes.ACCOUNT_NO_RECIPIENTS,
            }),
        );
        yield put(moneyTransferValidateStart());
    }
    yield put(moneyTransferCheckRecipientsFinish());
}

export function* moneyTransferValidateSaga(action?: TMoneyTransferValidateStartAction): SagaIterator {
    let isStepValid = true;
    let validateError = '';
    const stepName = yield select(makeSelectWizardStepName());
    switch (stepName) {
        case EMoneyTransferSteps.Account:
            isStepValid = yield call(moneyTransferValidateStepAccountSaga, action);
            break;
        case EMoneyTransferSteps.Final:
            // служебный пользователь может дойти до конца визрда, но завершить не может
            const isServiceUser: boolean = yield select(makeSelectAuthoritiesIsServiceUser());
            if (isServiceUser) {
                const commonTranslation: ICommonTranslation = yield select(makeSelectTranslationCommon());
                validateError = commonTranslation.serviceUser.tooltip;
                isStepValid = false;
                break;
            }
            isStepValid = yield call(wizardReportValidateSaga);
            break;
    }
    yield put(moneyTransferValidateSetStatus({ isStepValid, validateError }));
}

export function* moneyTransferValidateStepAccountSaga(action?: TMoneyTransferValidateStartAction): SagaIterator {
    let validateStatus = true;

    const transferAccountFrom: IAccountSelectItem = yield select(
        makeSelectMoneyTransferSelectedAccount(EMoneyTransferParamType.transferAccountFrom),
    );
    const transferAccountTo: IAccountSelectItem = yield select(
        makeSelectMoneyTransferSelectedAccount(EMoneyTransferParamType.transferAccountTo),
    );
    const transferAmount = yield select(makeSelectMoneyTransferAmount());
    const { transferAmountError, transferAccountFromError, transferAccountToError }: IMoneyTransferErrorsStepAccount =
        yield select(makeSelectMoneyTransferErrorsStepAccount());

    if (transferAccountFrom.id && transferAccountFrom.balance <= 0) {
        validateStatus = false;
        yield put(
            moneyTransferSetParamsErrorCode({
                accountFrom: EMoneyTransferValidationCodes.ACCOUNT_FROM_BALANCE_NEGATIVE,
            }),
        );
    } else if (action?.payload?.noRecipients) {
        validateStatus = false;
        yield put(
            moneyTransferSetParamsErrorCode({
                accountFrom: EMoneyTransferValidationCodes.ACCOUNT_NO_RECIPIENTS,
            }),
        );
    }

    if (transferAmount && parseMoneyToFloat(transferAmount) === 0) validateStatus = false;

    if (
        !transferAccountFrom.id ||
        !transferAccountTo.id ||
        !transferAmount ||
        transferAccountFrom.balance < parseMoneyToFloat(transferAmount)
    ) {
        validateStatus = false;
    }

    if (transferAccountFromError || transferAccountToError || transferAmountError) validateStatus = false;

    return validateStatus;
}

export function* moneyTransferStepInitSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const stepName = yield select(makeSelectWizardStepName());
    let accountFrom: IAccountSelectItem;

    const localStorageParams = get(localStorageState, `${basketSnapshotId}`, {
        conflicts: [],
        [EMoneyTransferParamType.transferAccountFrom]: { ...initTransferAccount },
        [EMoneyTransferParamType.transferAccountTo]: { ...initTransferAccount },
        transferAmount: '',
    });
    const accountTo: IAccountSelectItem = localStorageParams.transferAccountTo;
    const amount: string = localStorageParams.transferAmount;

    switch (stepName) {
        case EMoneyTransferSteps.Account:
            const validateOptions: IMoneyTransferValidateStartPayload = {};
            const { recipientsExist, ...metaAccountFrom } = get(action.payload, 'step.accounts.from') || {
                ...initTransferAccount,
            };

            if (recipientsExist !== undefined && !localStorageParams.transferAccountFrom.id)
                validateOptions.noRecipients = !recipientsExist;

            accountFrom = localStorageParams.transferAccountFrom.id
                ? localStorageParams.transferAccountFrom
                : metaAccountFrom;

            yield put(moneyTransferSetParams({ accountFrom, accountTo, amount }));
            if (localStorageParams.transferAccountFrom.id) yield put(moneyTransferCheckRecipientsStart());
            yield put(moneyTransferValidateStart(validateOptions));
            break;

        case EMoneyTransferSteps.Final:
            accountFrom = localStorageParams.transferAccountFrom;
            const conflictsStore = yield select(makeSelectMoneyTransferGetConflicts());
            const conflicts = conflictsStore.length ? conflictsStore : localStorageParams.conflicts;

            yield put(moneyTransferSetParams({ accountFrom, accountTo, amount }));
            yield put(moneyTransferSetConflictsAction(conflicts || []));
            yield call(wizardReportStorageRestoreSaga, action);
            yield put(moneyTransferValidateStart());
            break;
    }
}

type TMoneyTransferStepFailedSagaActions = TWizardStartFailedAction | TWizardNextFailedAction | TWizardBackFailedAction;

export function* moneyTransferStepFailedSaga(action: TMoneyTransferStepFailedSagaActions): SagaIterator {
    const validation = action.payload.response?.error?.validation;
    const errorCode = action.payload.response?.error?.code;
    const httpStatus = action.payload.code;
    let title = '';

    const stepName = yield select(makeSelectWizardStepName());
    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (validation && validation.length) {
        let errors: { [key: string]: string } = {};
        switch (stepName) {
            case EMoneyTransferSteps.Account:
                errors = parseWizardValidationErrors(validation, mapMoneyTransferStepsApiToParams);
                yield put(moneyTransferSetParamsErrorCode(errors));
                break;

            case EMoneyTransferSteps.Final:
                errors = parseWizardValidationErrors(validation, mapWizardReportApiToParams);
                yield put(wizardReportSetErrorsAction(errors));
                break;
        }
        yield put(moneyTransferValidateStart());
    }

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const moneyTransferTranslation: IMoneyTransferTranslation = yield select(makeSelectMoneyTransferTranslation());
        const wizardTranslation: IWizardTranslation = yield select(makeSelectWizardTranslation());
        title =
            moneyTransferTranslation.error.codes[errorCode] ||
            translationCommon.error.codes[errorCode] ||
            wizardTranslation.wizardModalError.default;
    }

    yield call(moneyTransferSummaryFailedSaga, title);
}

export function* moneyTransferSummaryFailedSaga(errorMessage: string): SagaIterator {
    const commonTranslation: ICommonTranslation = yield select(makeSelectTranslationCommon());

    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    title: errorMessage,
                    translation: commonTranslation.modal,
                }),
        }),
    );
}

export function* moneyTransferHandleNextSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    const amount: string = yield select(makeSelectMoneyTransferAmount());
    const from: string = (yield select(
        makeSelectMoneyTransferSelectedAccount(EMoneyTransferParamType.transferAccountFrom),
    )).id;
    const to: string = (yield select(makeSelectMoneyTransferSelectedAccount(EMoneyTransferParamType.transferAccountTo)))
        .id;

    const payloadNew: any = {};
    set(payloadNew, EMoneyTransferStepsApiParams.amount, parseMoneyToFloat(amount));
    set(payloadNew, EMoneyTransferStepsApiParams.from, from);
    set(payloadNew, EMoneyTransferStepsApiParams.to, to);

    const payload: IMoneyTransferNextAccountPayload | IMoneyTransferNextFinalPayload = payloadNew;

    switch (stepName) {
        case EMoneyTransferSteps.Account:
            yield put(wizardNextAction(payload));
            return;

        case EMoneyTransferSteps.Final:
            const reportParams: IWizardReportOrderParams = yield select(makeSelectWizardReportOrderParams());
            yield put(wizardNextAction({ ...payload, ...reportParams }));
            return;
    }
}

export function* moneyTransferStorageSaveStepSaga(action: TMoneyTransferStorageSaveStepAction): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams = localStorageState[basketSnapshotId];

    const transferAccountFrom: IAccountSelectItem = yield select(
        makeSelectMoneyTransferSelectedAccount(EMoneyTransferParamType.transferAccountFrom),
    );
    const transferAccountTo: IAccountSelectItem = yield select(
        makeSelectMoneyTransferSelectedAccount(EMoneyTransferParamType.transferAccountTo),
    );
    const transferAmount = yield select(makeSelectMoneyTransferAmount());
    const notificationEmail: IWizardReportParam = yield select(makeSelectWizardReportParam(EWizardReportParam.EMAIL));
    const notificationPhone: IWizardReportParam = yield select(makeSelectWizardReportParam(EWizardReportParam.PHONE));
    const conflicts: IWizardValidationError[] = yield select(makeSelectMoneyTransferGetConflicts());

    const params = {
        ...localStorageParams,
        conflicts,
        transferAccountFrom,
        transferAccountTo,
        transferAmount,
    };

    if (action.payload?.notificationEmail) params.notificationEmail = notificationEmail;
    if (action.payload?.notificationPhone) params.notificationPhone = notificationPhone;

    yield put(wizardStorageSaveStepAction({ params }));
}

export function* moneyTransferNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    switch (stepName) {
        case EMoneyTransferSteps.Account:
            const conflicts: IWizardValidationError[] = action.payload.currentStep?.step.conflicts || [];
            yield put(moneyTransferSetConflictsAction(conflicts));
            yield put(moneyTransferStorageSaveStep());
            return;
        case EMoneyTransferSteps.Final:
            const orderId = action.payload.currentStep?.step?.order?.orderId ?? '';

            const isMegaPowersFeatureGreaterOrEqualThan2 = yield select(
                makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.MEGA_POWERS, 2, true),
            );

            if (isMegaPowersFeatureGreaterOrEqualThan2) {
                const { orderCreationModal }: ICommonTranslation = yield select(makeSelectTranslationCommon());

                const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
                const goBack = () => breadCrumbGoBack(breadCrumbsData);

                yield put(
                    modalOpen({
                        render: () =>
                            createElement(ModalMultipleOrders, {
                                isOpen: true,
                                orderOrOrders: orderId,
                                onClose: goBack,
                                onSuccessButtonClick: breadCrumbGoBack,
                                orderTypeTranlation: orderCreationModal.success.orderType.MONEY_TRANSFER,
                            }),
                    }),
                );
            } else {
                yield put(wizardOpenModalSummaryAction({ orderId }));
            }
            return;
    }
}

export function* moneyTransferConflictsClearSaga() {
    yield put(moneyTransferSetConflictsAction([]));
    yield put(moneyTransferStorageSaveStep());
}

export function* moneyTransferSagas(): SagaIterator {
    yield all([
        takeEvery(MONEY_TRANSFER_LOAD_ACCOUNT_LIST, moneyTransferLoadAccountListSaga),
        takeEvery(MONEY_TRANSFER_HANDLE_NEXT, moneyTransferHandleNextSaga),
        takeEvery([WIZARD_BACK_FAILED, WIZARD_NEXT_FAILED, WIZARD_START_FAILED], moneyTransferStepFailedSaga),
        takeEvery(WIZARD_META_SUCCESS, moneyTransferStepInitSaga),
        takeEvery(WIZARD_NEXT_SUCCESS, moneyTransferNextSuccessSaga),
        takeEvery([WIZARD_BACK_SUCCESS, WIZARD_START_SUCCESS], moneyTransferConflictsClearSaga),
        takeEvery(MONEY_TRANSFER_VALIDATE_START, moneyTransferValidateSaga),
        takeEvery(MONEY_TRANSFER_STORAGE_SAVE_STEP, moneyTransferStorageSaveStepSaga),
        takeEvery(MONEY_TRANSFER_CHECK_RECIPIENTS_START, moneyTransferCheckRecipientsSaga),
    ]);
}
